import { Controller } from "@hotwired/stimulus"

class ScoreKeeper {
  static FIB = [1, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144, 233]

  constructor(startingFibIndex = 0) {
    this.total = 0
    this.guessCount = 0
    this.fibIndex = startingFibIndex
    this.letterUseCounts = {}
    this.hookedUseCounts = {}
  }

  compute(classification) {
    const baseFib = ScoreKeeper.FIB[Math.min(this.fibIndex, ScoreKeeper.FIB.length - 1)]
    const letterCounts = { ...this.letterUseCounts }
    let points = 0
    const breakdown = []

    for (let i = 0; i < classification.letters.length; i++) {
      if (classification.statuses[i] === 'locked') {
        breakdown.push({ letter: classification.letters[i], status: 'locked' })
      } else if (classification.statuses[i] === 'hooked') {
        const letter = classification.letters[i]
        this.hookedUseCounts[letter] = (this.hookedUseCounts[letter] || 0) + 1
        const earned = this.hookedUseCounts[letter]
        points += earned
        breakdown.push({ letter, status: 'hooked', earned })
      } else {
        const letter = classification.letters[i]
        const uses = letterCounts[letter] || 0
        const penalty = Math.min(0, (uses * (uses + 1) / 2))
        const earned = Math.max(1, baseFib - penalty)
        points += earned
        breakdown.push({ letter, status: 'normal', uses, penalty, earned })
        this.letterUseCounts[letter] = (this.letterUseCounts[letter] || 0) + 1
      }
    }

    this._lastBreakdown = { word: classification.letters.join(''), baseFib, breakdown, total: points }
    return points
  }

  toJSON() {
    return {
      total: this.total,
      guessCount: this.guessCount,
      fibIndex: this.fibIndex,
      letterUseCounts: { ...this.letterUseCounts },
      hookedUseCounts: { ...this.hookedUseCounts }
    }
  }

  static fromJSON(data) {
    const sk = new ScoreKeeper()
    sk.total = data.total
    sk.guessCount = data.guessCount
    sk.fibIndex = data.fibIndex
    sk.letterUseCounts = { ...data.letterUseCounts }
    sk.hookedUseCounts = { ...(data.hookedUseCounts || {}) }
    return sk
  }
}

export default class extends Controller {
  initialize() {
    this.scoreKeeper = new ScoreKeeper()
  }

  compute(classification) {
    const points = this.scoreKeeper.compute(classification)
    debugScoring(this.scoreKeeper._lastBreakdown)
    return points
  }

  add(points) {
    this.scoreKeeper.total += parseInt(points)
    this.scoreKeeper.guessCount++
    this.scoreKeeper.fibIndex++
  }

  penalize() {
    this.scoreKeeper.fibIndex = Math.max(0, this.scoreKeeper.fibIndex - 1)
  }

  get total() {
    return this.scoreKeeper.total
  }

  get fibIndex() {
    return this.scoreKeeper.fibIndex
  }

  get currentMultiplier() {
    return ScoreKeeper.FIB[Math.min(this.scoreKeeper.fibIndex, ScoreKeeper.FIB.length - 1)]
  }

  get letterUseCounts() {
    return this.scoreKeeper.letterUseCounts
  }

  renderTotal() {
    this.element.textContent = this.scoreKeeper.total
  }

  reset(wordLength = 5) {
    const startingFibIndex = Math.max(0, 6 - wordLength)
    this.scoreKeeper = new ScoreKeeper(startingFibIndex)
    this.renderTotal()
  }

  snapshot() {
    return this.scoreKeeper.toJSON()
  }

  restore(data) {
    this.scoreKeeper = ScoreKeeper.fromJSON(data)
    this.renderTotal()
  }
}

// --- DEBUG: score breakdown (remove this function + the call in compute() above) ---
function debugScoring({ word, baseFib, breakdown, total }) {
  const lines = [`Guessed ${word}, base multiplier: x${baseFib}`]

  for (const entry of breakdown) {
    if (entry.status === 'locked') {
      lines.push(`${entry.letter} (LOCKED): => +0`)
    } else if (entry.status === 'hooked') {
      lines.push(`${entry.letter} (HOOKED): => +${entry.earned}`)
    } else {
      lines.push(`${entry.letter} (used ${entry.uses} times): ${baseFib} - ${entry.penalty} => +${entry.earned}`)
    }
  }

  lines.push(`TOTAL: +${total}`)
  console.log(lines.join('\n'))
}
